import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { Box, Button, TextField, Typography, Paper, Alert } from '@mui/material';
import api from '../api/axiosConfig';

const RestorePass: React.FC = () => {
  const [token, setToken] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const urlToken = params.get('token');
    if (urlToken) setToken(urlToken);
  }, [location.search]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    if (!token || !newPassword || !confirmPassword) {
      setError('All fields are required.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }
    setLoading(true);
    try {
      await api.post('/api/auth/restore-pass', {
        token,
        newPassword,
        confirmPassword,
      });
      setSuccess('Password set successful! You can now log in.');
      setTimeout(() => navigate('/login'), 2000);
    } catch (err: any) {
      let message = err.response?.data?.ErrorMessage || err.response?.data?.message || 'Failed to set password.';
      if (err.message && message === 'Failed to set password.') {
        message = err.message;
      }
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      display="flex"
      minHeight="100vh"
      width="100vw"
      sx={{ background: '#fff' }}
    >
      {/* Left image section */}
      <Box
        flex={1}
        display={{ xs: 'none', md: 'flex' }}
        alignItems="center"
        justifyContent="center"
        sx={{
          background: 'linear-gradient(135deg, #e3f0ff 0%, #f9f9f9 100%)',
          minHeight: '100vh',
          width: '50vw',
          p: 0,
          m: 0,
          overflow: 'hidden',
          position: { md: 'fixed' },
          left: 0,
          top: 0,
          bottom: 0,
          height: '100vh',
          zIndex: 1,
        }}
      >
        <img
          src="/paas.png"
          alt="Prediction as a Service"
          style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 0, boxShadow: 'none' }}
          onError={e => { (e.target as HTMLImageElement).src = '/logo_black.png'; }}
        />
      </Box>
      {/* Right restore-password section */}
      <Box
        flex={1}
        display="flex"
        alignItems="center"
        justifyContent="center"
        width={{ xs: '100vw', md: '50vw' }}
        minHeight="100vh"
        sx={{ background: '#fff', ml: { md: '50vw' }, zIndex: 2 }}
      >
        <Paper elevation={3} sx={{ p: 6, maxWidth: 500, width: '100%' }}>
          <Typography variant="h5" mb={2} align="center">Assign Password</Typography>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
          <form onSubmit={handleSubmit}>
            {/* Only show token field if not present in URL */}
            {!token && (
              <TextField
                label="Token"
                value={token}
                onChange={e => setToken(e.target.value)}
                fullWidth
                required
                margin="normal"
              />
            )}
            <TextField
              label="New Password"
              type="password"
              value={newPassword}
              onChange={e => setNewPassword(e.target.value)}
              fullWidth
              required
              margin="normal"
            />
            <TextField
              label="Confirm Password"
              type="password"
              value={confirmPassword}
              onChange={e => setConfirmPassword(e.target.value)}
              fullWidth
              required
              margin="normal"
            />
            <Button type="submit" variant="contained" color="primary" fullWidth sx={{ mt: 2 }} disabled={loading}>
              {loading ? 'Setting...' : 'Setting Password'}
            </Button>
          </form>
          <Box display="flex" justifyContent="center" mt={2}>
            <Link to="/login">Back to Login</Link>
          </Box>
        </Paper>
      </Box>
    </Box>
  );
};

export default RestorePass; 